/**
 * Pro / monetização (F1 — extração, ADR 0044): paywall (grátis + gerenciar plano),
 * ofertas contextuais (pro-offer), oferta de boas-vindas (welcome-offer), win-back
 * do vitalício, celebração pós-compra (pro-welcome), momento de valor e aviso de
 * rebaixamento. Valores são as strings PT literais das telas.
 *
 * Os TEXTOS de plano e de feature (title/description/period/tagline) vêm daqui — as chaves
 * `plans.*`, `features.*` e `chips.labels.*` são resolvidas em lib/pro/plans.ts (getProPlans,
 * getProFeatures) e no ProFeatureChips. Só os PREÇOS de fallback (priceLabel: "R$ 79,90") e os
 * nomes/valores vindos do RevenueCat NÃO entram aqui — chegam como placeholder
 * ({{price}}, {{plan}}, {{location}}...).
 */
export const pro = {
    common: {
        notNow: 'Agora não',
        restorePurchase: 'Restaurar compra',
        seeAllPlans: 'Ver todos os planos',
        opening: 'Abrindo...',
        pleaseWait: 'Aguarde...',
        popularBadge: 'MAIS POPULAR',
        trialFallbackPeriod: '7 dias',
        startTrialCta: 'Começar {{period}} grátis',
        startTrialCtaPlain: 'Começar teste grátis',
        trialReassurance: '{{price}} hoje · avisamos no dia {{day}} · cancele quando quiser',
        termsOfUse: 'Termos de Uso',
        privacyPolicy: 'Política de Privacidade',
    },
    // Textos dos planos (resolvidos em getProPlans, lib/pro/plans.ts). Preço fica lá (fallback) ou vem do RevenueCat.
    plans: {
        monthly: { title: 'Mensal', period: '/mês' },
        annual: { title: 'Anual', period: '/ano', tagline: 'Melhor custo' },
        lifetime: { title: 'Vitalício', period: 'uma vez' },
    },
    // Textos das features do Pro (resolvidos em getProFeatures, lib/pro/plans.ts), por ícone estável.
    features: {
        chart: {
            title: 'Relatórios avançados com PDF',
            description: 'Fixas vs variáveis, gastos por dia da semana, orçado vs realizado e PDFs prontos para compartilhar.',
        },
        forecast: {
            title: 'Fluxo de caixa',
            description: 'Acompanhe entradas e saídas ao longo do tempo e veja para onde o seu dinheiro está indo.',
        },
        compare: {
            title: 'Comparativo entre períodos',
            description: 'Compare meses lado a lado e enxergue a evolução dos seus gastos e receitas.',
        },
        scales: {
            title: 'Resultado líquido por categoria',
            description: 'Quanto cada categoria realmente pesou no mês: entradas menos saídas, sem se enganar.',
        },
        table: {
            title: 'Exportar para planilha',
            description: 'Baixe resumo mensal por conta ou todas as transações em CSV para Excel e Google Sheets.',
        },
        handcoins: {
            title: 'Quem me deve',
            description: 'Registre o que alguém te deve — empréstimo, venda, racha de conta — e acompanhe o saldo por pessoa e as datas combinadas, sem misturar com seus gastos.',
        },
        import: {
            title: 'Importação de extratos',
            description: 'Traga seu histórico em CSV, OFX, PDF ou prints do extrato — soma ao que já existe, sem apagar nada.',
        },
        reconcile: {
            title: 'Conferência de fatura ilimitada',
            description: 'Compare a fatura do cartão com o app, ache o que falta ou divergiu e lance tudo em um toque.',
        },
        bell: {
            title: 'Lembretes personalizados',
            description: 'Escolha horários dos vencimentos e intervalos de inatividade. Ligar e desligar continua grátis.',
        },
        widget: {
            title: 'Widgets na tela inicial',
            description: 'Saldo, entradas e saídas do mês sempre à vista, sem abrir o app.',
        },
        lightning: {
            title: 'Atalhos na home',
            description: 'Monte uma fileira de atalhos pras áreas que você mais usa no dia a dia.',
        },
        house: {
            title: 'Tela inicial do seu jeito',
            description: 'Reordene os blocos da home e mostre ou esconda o que você quer ver primeiro.',
        },
        calendar: {
            title: 'Mês começando no seu dia',
            description: 'Do dia do salário à véspera do próximo: Home, Transações, Análise e relatórios no seu período, não no mês do calendário.',
        },
        palette: {
            title: 'Temas e aparência',
            description: 'Dez paletas claras e escuras mais a cor de destaque pra deixar o Kontto com a sua cara.',
        },
        sync: {
            title: 'Sincronização entre aparelhos',
            description: 'Use a mesma conta no celular e no tablet — contas, transações e tudo mais ficam em dia nos dois, cifrados de ponta a ponta.',
        },
        target: {
            title: 'Tudo sem limite',
            description: 'Crie contas, cartões, categorias, tags, metas e orçamentos sem teto para deixar o Kontto do seu jeito.',
        },
        folder: {
            title: 'Backup automático na sua pasta',
            description: 'Um backup cifrado é gravado sozinho numa pasta do seu aparelho, com histórico das últimas versões.',
        },
        star: {
            title: 'Apoie um desenvolvedor independente',
            description: 'O Kontto é feito por uma pessoa só. Sua assinatura mantém o projeto vivo.',
        },
    },
    purchase: {
        unavailableTitle: 'Compras indisponíveis',
        unavailableOpenMessage: 'Não foi possível abrir a compra agora. Tente novamente em instantes.',
        unavailableTrialMessage: 'Não foi possível iniciar o teste agora. Tente novamente em instantes.',
        restored: 'Compras restauradas',
        nothingToRestore: 'Nada para restaurar',
        noActivePurchaseMessage: 'Não encontramos uma compra ativa nesta conta.',
        restoreFailed: 'Não foi possível restaurar',
    },
    // Período de teste/intro do RevenueCat (formatIntroPeriod em lib/pro/proService.ts).
    period: {
        day_one: '{{count}} dia',
        day_other: '{{count}} dias',
        week_one: '{{count}} semana',
        week_other: '{{count}} semanas',
        month_one: '{{count}} mês',
        month_other: '{{count}} meses',
        year_one: '{{count}} ano',
        year_other: '{{count}} anos',
    },
    paywall: {
        heroTitle: 'Controle total.\nAté no preço.',
        heroSubtitle: 'Relatórios, fluxo de caixa e tudo sem limite.',
        entryBadge: 'OFERTA DE ENTRADA',
        trialBadge: '{{period}} GRÁTIS',
        annualTitle: 'Anual',
        annualEntrySubtitle: '1º ano por {{intro}} · depois {{full}}/ano',
        annualSubtitle: 'Uma cobrança de {{price}} por ano. Só isso.',
        perFirstYear: '/1º ano',
        entrySavings: '{{pct}}% off no 1º ano',
        perMonth: '/mês',
        annualSavings: '{{pct}}% mais barato que o mensal',
        perYear: '/ano',
        monthlyTitle: 'Mensal',
        monthlySubtitle: 'Flexível · cancele quando quiser',
        founderBadge: 'FUNDADOR',
        lifetimeTitle: 'Vitalício',
        lifetimeSubtitle: 'Pague uma vez, nunca mais.',
        founderNote: 'Preço de fundador — vai subir',
        onceForever: 'uma vez, pra sempre',
        ctaLifetime: 'Comprar Vitalício · {{price}}',
        ctaEntry: 'Aproveitar · {{price}} no 1º ano',
        ctaAnnual: 'Assinar · {{price}}/ano',
        ctaMonthly: 'Assinar · {{price}}/mês',
        footerLifetime: 'Pagamento único. Sem renovação, sem cobranças futuras.',
        footerEntry: '{{intro}} hoje, pelo 1º ano · depois renova por {{full}}/ano até você cancelar em {{location}}.',
        footerTrialNoReminder: '{{price}} hoje · cancele quando quiser',
        footerAnnual: 'Renova por {{price}}/ano até você cancelar em {{location}}.',
        footerMonthly: 'Renova por {{price}}/mês até você cancelar em {{location}}.',
        trust: {
            priceLockedStrong: 'Preço travado.',
            priceLockedRest: 'Não sobe depois que você assina.',
            noSurpriseStrong: 'Sem surpresa.',
            noSurpriseRest: 'Avisamos antes de qualquer cobrança.',
            noLockStrong: 'Sem prisão.',
            noLockRest: 'Cancela em 2 toques, na {{store}}.',
        },
    },
    // "Tenho um cupom" no paywall (ADR 0209).
    coupon: {
        link: 'Tenho um cupom',
        title: 'Tenho um cupom',
        subtitle: 'Digite a palavra que você recebeu.',
        placeholder: 'Palavra do cupom',
        apply: 'Aplicar',
        checking: 'Conferindo...',
        invalid: 'Não achamos esse cupom. Confira a palavra.',
        expired: 'Esse cupom acabou em {{date}}.',
        seePlans: 'Ver os planos do Pro',
        offline: 'Sem internet. Conecte para conferir o cupom.',
        unavailable: 'Não deu para conferir o cupom agora. Tente de novo em instantes.',
        retry: 'Tentar de novo',
        storeDownTitle: 'A {{store}} não respondeu',
        storeDownBody: 'O cupom vale, mas os preços não chegaram. Tente de novo em instantes.',
        proTitle: 'Você já é Pro',
        proBody: 'O cupom {{code}} é para quem ainda não tem o Kontto Pro. Seu plano continua igual.',
        gotIt: 'Entendi',
        applied: 'Cupom {{code}} aplicado',
        validUntil: 'Válido até {{date}}',
        remove: 'Remover',
        badge: 'CUPOM -{{pct}}%',
        badgeNoPct: 'CUPOM',
        confirmInAppStore: 'Você confirma na App Store',
        ctaAppStore: 'Continuar na App Store',
        footerAppStore: 'Abrimos a App Store com o cupom preenchido. Confirme lá e volte: o Pro já vem ligado.',
        notConfirmed: 'Ainda não vimos a confirmação. Se você já confirmou na App Store, leva alguns segundos.',
        openAgain: 'Abrir a App Store de novo',
        // Padrão date-fns das datas do cupom ("30/11").
        datePattern: 'dd/MM',
    },
    // Carteirinha de Membro Fundador: topo da tela de plano de quem tem o vitalício.
    founderCard: {
        title: 'Membro Fundador',
        lifetime: 'Vitalício',
        lifetimeSince: 'Vitalício · desde {{date}}',
        // Padrão date-fns do "desde" (sai "set/2026").
        sinceDatePattern: 'MMM/yyyy',
        promise: 'Sem mensalidade.\nSem renovação. Pra sempre.',
    },
    manage: {
        title: 'Gerenciar seu plano',
        currentPlan: 'Plano atual',
        annualPerMonth: '≈ {{price}}/mês',
        annualPerMonthSavings: '≈ {{price}}/mês • economize {{pct}}%',
        manageStorePurchases: 'Gerenciar compras na loja',
        recurringNote: 'Cobrança recorrente. Cancele quando quiser.',
        orPayOnce: 'ou pague uma vez',
        lifetimeOptionTitle: '{{plan}} — pague uma vez',
        lifetimeOptionSubtitle: 'Pra sempre, sem renovação. Preço de Membro Fundador — vai subir.',
        manageOrCancel: 'Gerenciar ou cancelar na loja',
        featuresTitle: 'O que o Kontto Pro inclui',
        ctaLifetime: 'Comprar Vitalício',
        ctaSwitch: 'Mudar para {{plan}}',
        fineprintLifetime: 'Pagamento único, sem renovação.',
        fineprintRenews: '{{planLabel}} · renova automaticamente até você cancelar em {{location}}.',
        lifetimeToastTitle: 'Vitalício ativado 💛',
        lifetimeToastMessage: 'Cancele sua assinatura atual em {{location}} pra não ser cobrado de novo.',
        // Vitalício com trial/assinatura ainda ativa ao lado (ADR 0089): o entitlement espelha a
        // assinatura até ela acabar; a tela avisa o que acontece e o que fazer.
        lifetimeSubRenewsTitle: 'Sua assinatura ainda renova',
        lifetimeSubTrialRenewsBody:
            'Seu teste grátis do plano recorrente segue ativo até {{date}} — e quando acabar, a {{store}} vai cobrar a assinatura. Ela não cancela sozinha: cancele em {{location}}.',
        lifetimeSubPaidRenewsBody:
            'Sua assinatura recorrente segue ativa até {{date}} e vai renovar. Ela não cancela sozinha: cancele em {{location}} pra não pagar de novo.',
        lifetimeSubCancelCta: 'Cancelar na {{store}}',
        lifetimeSubEndingTitle: 'Tudo certo por aqui',
        lifetimeSubTrialEndingBody:
            'Seu teste grátis termina em {{date}} sem cobrança. O vitalício continua — nada a fazer.',
        lifetimeSubPaidEndingBody:
            'Sua assinatura termina em {{date}} e não renova. O vitalício continua — nada a fazer.',
        planUpdated: 'Plano atualizado',
        openStoreFailed: 'Não foi possível abrir',
    },
    offer: {
        expensesFallback: 'Seus gastos',
        expenses_one: 'Seu {{count}} gasto',
        expenses_other: 'Seus {{count}} gastos',
        forecastTitle: 'Seu fluxo de caixa\njá existe. Ele é Pro.',
        forecastSubtitle: 'Entradas e saídas dos seus lançamentos ao longo do tempo — pra onde seu dinheiro está indo. {{trial}} grátis pra ver o seu.',
        forecastCta: 'Ver meu fluxo — {{trial}} grátis',
        exportTitle: 'Sua planilha\njá está pronta. Ela é Pro.',
        exportSubtitle: '{{expenses}} em CSV pra Excel e Google Sheets, com resumo mensal por conta. {{trial}} grátis pra baixar a sua.',
        // CTAs longos perderam o "— {{trial}} grátis": acima de ~30 chars o rótulo
        // encolhia até o piso do autoFitFontSize (10px). O trial segue anunciado no
        // subtítulo e na linha de segurança logo abaixo do botão (trialReassurance).
        exportCta: 'Exportar minha planilha',
        bellSubFallback: 'Sua assinatura',
        bellTitleNext: '{{name}} renova {{date}}.\nQuer o aviso 1 dia antes?',
        bellTitle: '{{name}} vai renovar.\nQuer o aviso 1 dia antes?',
        bellSubtitle: 'O Pro avisa 1 dia antes de cada cobrança — {{name}} e as próximas. Nunca mais assinatura esquecida na fatura.',
        bellCta: 'Ativar avisos — {{trial}} grátis',
        backupTitle: '{{transactions}} transações em {{days}} dias.\nTudo num aparelho só.',
        backupSubtitle: 'Celular quebrou ou sumiu, o histórico vai junto. O Pro grava um backup cifrado automático, com histórico de versões — sem você lembrar de nada.',
        backupCta: 'Proteger meu histórico',
        importTitle: 'Seu histórico de anos,\ndentro do Kontto em minutos.',
        importSubtitle: 'Importe CSV, OFX, extrato em PDF ou até prints — os lançamentos entram somando ao que você já tem, sem apagar nada. {{trial}} grátis pra trazer o seu.',
        importCta: 'Importar meu histórico',
        reconcileTitle: 'Bateu a fatura?\nO Kontto confere pra você.',
        reconcileSubtitle: 'Envie a fatura do cartão (PDF ou prints) e veja na hora o que falta lançar ou divergiu — e corrija tudo em um toque. {{trial}} grátis pra conferir a sua.',
        reconcileCta: 'Conferir minha fatura',
        reportTitle: 'Seu relatório já existe.\nEle é Pro.',
        // Concordância "vira/viram" escolhida por ternário explícito (0 gasto usa "viram",
        // mas o plural do i18next em pt trata 0 como "one" — quebraria a neutralidade).
        reportSubtitleOne: '{{expenses}} vira isto: categorias, comparativo com o mês anterior e PDF. {{trial}} grátis pra ver o seu.',
        reportSubtitleMany: '{{expenses}} viram isto: categorias, comparativo com o mês anterior e PDF. {{trial}} grátis pra ver o seu.',
        reportCta: 'Ver meu relatório',
        recordedInMonth: 'Você registrou em {{month}}',
        inExpenses_one: 'em {{count}} gasto',
        inExpenses_other: 'em {{count}} gastos',
        renewsOn: 'renova {{date}}',
        renewalNote: 'Depois do teste: Pro Anual por {{price}}/ano, renovado automaticamente até você cancelar.',
    },
    proWelcome: {
        closeA11y: 'Fechar',
        title: 'Bem-vindo ao Kontto Pro! 🎉',
        subtitle: 'Tudo desbloqueado. Obrigado por apoiar um desenvolvedor independente 💛',
        moreFeatures: 'Ver os outros {{extra}} recursos',
        notifBlockedText: 'Pra te avisar antes de qualquer cobrança e nos vencimentos, ative as notificações.',
        notifEnableButton: 'Ativar',
        startButton: 'Começar',
    },
    welcomeOffer: {
        ctaSubscribe: 'Assinar Pro Anual',
        ctaSubscribeWithPrice: 'Assinar Pro Anual · {{price}}',
        activationTitle: 'Esse foi 1.\nAgora veja o mês inteiro.',
        activationSubtitleLead: '{{lead}} — o Pro transforma seus gastos nisso. {{trial}} grátis.',
        activationSubtitle: 'O Pro transforma seus gastos em relatórios e fluxo de caixa. {{trial}} grátis.',
        heroTitle: '{{trial}} de Pro,\nsem pegadinha',
        heroSubtitle: 'Veja exatamente como o teste funciona.',
        // Hard paywall do iOS (design 4g, ADR 0047): tela obrigatória pós-onboarding.
        hardTitle: 'Seu Kontto tá pronto.\nComece com {{trial}} grátis.',
        hardSubtitle: 'Tudo liberado hoje por {{price}}. Veja como funciona:',
        hardNoTrialTitle: 'Seu Kontto tá pronto.\nAssine e libere tudo.',
        hardSeeAllPlans: 'Ver todos os planos',
        hardHelp: 'Precisa de ajuda?',
        noTrialTitle: 'Kontto Pro,\nsem pegadinha',
        noTrialSubtitle: 'Você já usou seu período de teste. A assinatura libera tudo agora — renovação anual, cancele quando quiser.',
        conversionFull: '{{perMonth}}/mês, cobrado {{annual}} uma vez por ano. Cancele antes e não paga nada.',
        conversionAnnual: 'Cobrado {{annual}} uma vez por ano. Cancele antes e não paga nada.',
        conversionBare: 'Cancele antes e não paga nada.',
        timelineTodayTitle: 'Hoje — tudo liberado',
        timelineTodayTextNoTrial: 'Relatórios, PDF, fluxo de caixa e sem limites, na hora.',
        timelineTodayText: 'Relatórios, PDF, fluxo de caixa e sem limites. {{price}}.',
        timelineRenewalTitle: 'Renovação anual',
        timelineRenewalTextFull: '{{perMonth}}/mês, cobrado {{annual}} uma vez por ano. Cancele quando quiser.',
        timelineRenewalText: 'Cobrado uma vez por ano. Cancele quando quiser.',
        timelineReminderTitle: 'Dia {{day}} — a gente te avisa',
        timelineReminderText: 'Lembrete de que o teste está acabando. Sem surpresa na fatura.',
        timelineConvertTitle: 'Dia {{day}} — vira Pro Anual',
        noTrialReassurance: 'Cobrança hoje, renovação anual · cancele quando quiser',
    },
    winback: {
        // Contador REAL: a oferta expira em 24h (ver lib/pro/winbackOffer.ts). Sem prazo de
        // verdade, o contador seria propaganda enganosa (Apple 3.1.2).
        countdownLead: 'Essa oferta some em',
        timeHm: '{{hours}}h {{minutes}}min',
        timeM: '{{minutes}}min',
        // Desconto CALCULADO dos dois preços reais da loja — nunca fixo.
        badge: '-{{discount}}%',
        // `regularPrice` = preço do SKU cheio (kontto_pro_lifetime) vindo do offering `default`.
        metaPaymentOnce: 'Pagamento único pela {{store}}',
        metaForever: 'Acesso pra sempre',
        metaAfter: 'Depois, o Vitalício volta a {{regularPrice}}.',
        legalOnce: 'Só aparece uma vez.',
        // Variante padrão — dormente que nunca pagou (trial expirado incluso).
        default: {
            title: 'Kontto Pro pra sempre,\npor um pagamento só',
            subtitle: 'Sem mensalidade, nunca mais.',
            cta: 'Ficar com o Pro pra sempre',
            decline: 'Agora não',
        },
        // Variante renewal_off — assinante MENSAL pago que desligou a renovação na loja.
        // `date` = expirationDate da assinatura (dd/MM); `price` = vitalício promocional.
        renewalOff: {
            title: 'Seu Pro termina dia {{date}}.\nFica pra sempre por {{price}}?',
            titleNoDate: 'Seu Pro está pra terminar.\nFica pra sempre por {{price}}?',
            subtitle: 'Um pagamento só, menos que 9 mensalidades. Depois disso, nunca mais uma cobrança.',
            // Verdade garantida pelo gatilho (willRenew === false): não existe cobrança dupla.
            noDoubleCharge: 'Sua mensalidade já não renova. Sem cobrança dupla.',
            keepEverything: 'Tudo que você configurou continua: temas, widgets, tela inicial do seu jeito.',
            // Android-only (a feature de backup automático não existe no iOS — catálogo em plans.ts).
            keepBackup: 'Não perca seus dados: o backup automático segue rodando.',
            keepReports: 'Relatórios avançados, exportação em PDF e tudo sem limite.',
            cta: 'Ficar com o Pro pra sempre',
            // CTA secundário: troca de plano (proração pela loja). Preços vêm do offering.
            switchAnnualLead: 'Prefere assinar?',
            switchAnnualCta: 'Trocar pro Anual · {{annualPrice}}/ano',
            switchAnnualPerMonth: 'dá {{perMonth}}/mês',
            decline: 'Deixar terminar dia {{date}}',
            declineNoDate: 'Deixar terminar',
        },
        // Variante churned — já pagou (mensal ou anual) e expirou. Fala com quem cancelou por
        // ter sido cobrado sem querer: o vitalício tira a mensalidade da frente.
        churned: {
            title: 'Volte pro Pro, desta vez pra sempre.\n{{price}}, um pagamento só.',
            subtitle: 'Sem mensalidade pra vigiar. Paga uma vez e fica.',
            // Ternário explícito no componente (count === 1) — o plural pt do i18next trata 0
            // como "one"; mostrar só com count >= 1, senão `dataFallback`.
            dataSingular: 'Seu {{count}} lançamento e seus relatórios estão aqui, do jeito que você deixou.',
            dataPlural: 'Seus {{count}} lançamentos e relatórios estão aqui, do jeito que você deixou.',
            dataFallback: 'Seus dados e relatórios estão aqui, do jeito que você deixou.',
            // Android-only (idem acima) — e é literal: o downgrade PARA o backup automático.
            backBackup: 'O backup automático volta a rodar: não perca seus dados.',
            backReports: 'Relatórios avançados com PDF e tudo sem limite, de volta.',
            backLook: 'Temas, widgets e a tela inicial do seu jeito.',
            cta: 'Voltar pro Pro pra sempre',
            decline: 'Agora não',
        },
        banner: {
            title: 'Pro Vitalício',
            pill: '24 HORAS',
            subtitle: 'Pague uma vez, use pra sempre',
            // renewal_off: o banner cita a data real em vez do slogan.
            subtitleRenewalOff: 'Seu Pro termina dia {{date}}. Fique pra sempre por {{price}}',
            countdown: 'Termina em {{time}}',
            accessibility: 'Kontto Pro Vitalício por {{price}}. A oferta termina em {{time}}.',
        },
        // Modo ANUAL (ADR 0174): o mesmo anual do paywall com o 1º ano mais barato, no lugar do
        // vitalício promocional. `price` = 1º ano; `regularPrice` = anual cheio (renovação);
        // `perMonth` = 1º ano ÷ 12. Tudo vem da loja. Os "Agora não" são os da variante.
        annual: {
            metaFirstYear: '1º ano por {{price}} pela {{store}}',
            metaCancel: 'Cancele quando quiser',
            // A Apple exige o preço de renovação visível na tela de compra.
            metaAfter: 'Depois, renova a {{regularPrice}} por ano.',
            default: {
                title: 'Kontto Pro por {{price}}\nno primeiro ano',
                subtitle: 'Dá {{perMonth}} por mês.',
                cta: 'Assinar com desconto',
            },
            renewalOff: {
                title: 'Seu Pro termina dia {{date}}.\nFica mais um ano por {{price}}?',
                titleNoDate: 'Seu Pro está pra terminar.\nFica mais um ano por {{price}}?',
                subtitle: 'Troque a mensalidade pelo anual com desconto: dá {{perMonth}} por mês.',
                cta: 'Trocar pro anual com desconto',
            },
            churned: {
                title: 'Volte pro Pro por {{price}}\nno primeiro ano',
                subtitle: 'Dá {{perMonth}} por mês, com tudo do Pro de volta.',
                cta: 'Voltar pro Pro',
            },
            banner: {
                title: 'Pro Anual',
                subtitle: 'Primeiro ano com desconto',
                subtitleRenewalOff: 'Seu Pro termina dia {{date}}. Fique mais um ano por {{price}}',
                accessibility: 'Kontto Pro Anual por {{price}} no primeiro ano. A oferta termina em {{time}}.',
            },
        },
    },
    valueMoment: {
        title: 'Você está no controle',
        subtitle: 'Manter as contas em dia é o hábito que muda o jogo. Olha o seu progresso:',
        // Ternário explícito em vez de _one/_other: o plural pt do i18next trata 0 como
        // "one" e mudaria o rótulo de contagens zeradas (extração é comportamento-neutra).
        daysSingular: 'dia no controle',
        daysPlural: 'dias no controle',
        txSingular: 'lançamento registrado',
        txPlural: 'lançamentos registrados',
        settledSingular: 'conta quitada',
        settledPlural: 'contas quitadas',
        proLineLead: '{{lead}}, o Kontto Pro vai além: relatórios avançados, backup e o app do seu jeito.',
        proLine: 'O Kontto Pro leva isso além: relatórios avançados, backup e o app do seu jeito.',
        cta: 'Conhecer o Kontto Pro',
    },
    downgrade: {
        title: 'Seu Kontto Pro expirou',
        subtitle: 'A partir de agora:',
        reportBlockedCount: 'Seu relatório de {{month}} está bloqueado — com os {{expenses}} gastos que você registrou.',
        reportBlocked: 'Seu relatório de {{month}} está bloqueado.',
        backupStoppedLast: 'O backup automático parou de rodar. O último foi {{distance}}.',
        backupStopped: 'O backup automático parou de rodar.',
        themeReset: 'Seu tema voltou ao padrão.',
        monthStartReset: 'O mês voltou a começar no dia 1.',
        limitsBack: 'Criar contas, cartões, metas e orçamentos voltou a ter limite.',
        footnote: 'Nada foi apagado: seus dados e histórico continuam no aparelho. Reative e tudo volta como estava, do jeitinho que você deixou.',
        cta: 'Reativar Pro',
    },
    // Teste grátis cancelado (ADR 0173): tela "o que pesou?" (/trial-cancelled) e resumo do
    // último dia (/trial-summary). Sem travessão na copy.
    trialCancel: {
        title: 'Pronto, você não vai ser cobrado',
        subtitle: 'Seu Kontto Pro continua liberado até {{date}}. Aproveite os dias que faltam.',
        question: 'Se puder, conta pra gente: o que pesou?',
        reasons: {
            forget: 'Cancelei só pra não esquecer',
            price: 'Achei caro',
            use: 'Ainda não usei o suficiente',
            missing: 'Faltou alguma coisa no app',
        },
        footnote: 'Um toque e pronto. Sua resposta ajuda a decidir o que vem no app.',
        notNow: 'Agora não',
        close: 'Fechar',
        today: 'hoje',
        tomorrow: 'amanhã',
        understood: 'Entendi',
        forget: {
            title: 'Combinado',
            body: 'A gente te avisa {{day}}, um dia antes do fim. Até lá, todo o Pro segue liberado.',
            bodyNoReminder: 'Até {{date}}, todo o Pro segue liberado.',
            today: 'Hoje',
            todayText: 'Teste cancelado. Nada será cobrado.',
            reminderText: 'Lembrete com o resumo do seu teste',
            endText: 'O Pro termina. Seus dados continuam aqui.',
            invoiceTitle: 'Enquanto isso, confira sua fatura',
            invoiceBody: 'Bata cada compra com o extrato do banco',
            reportTitle: 'Enquanto isso, veja seu relatório',
            reportBody: 'Categorias, fluxo de caixa e PDF',
        },
        price: {
            title: 'Tem o plano mensal',
            body: 'Sem pagar o ano de uma vez. Você paga mês a mês e cancela quando quiser.',
            planName: 'Kontto Pro Mensal',
            badge: 'SEM FIDELIDADE',
            perMonth: '/mês',
            note: 'Quando o teste acabar, ele já aparece selecionado pra você.',
            footnote: 'O teste que você cancelou vai até {{date}} e não será cobrado.',
        },
        use: {
            title: 'Ainda dá tempo de testar',
            body_one: 'Falta {{count}} dia de Pro. Comece por uma destas:',
            body_other: 'Faltam {{count}} dias de Pro. Comece por uma destas:',
            invoiceTitle: 'Conferir a fatura',
            invoiceBody: 'Bata cada compra com o extrato do banco',
            reportTitle: 'Relatório de {{month}}',
            reportBody: 'Categorias, fluxo de caixa e PDF',
            backupTitle: 'Backup automático',
            backupBody: 'Uma cópia na pasta que você escolher',
            ctaInvoice: 'Começar pela fatura',
            ctaReport: 'Ver meu relatório',
        },
        missing: {
            title: 'O que faltou?',
            body: 'Toque no que mais fez falta. Entra direto na lista do que vem por aí.',
            wants: {
                bank: 'Puxar gastos direto do banco',
                web: 'Usar no computador',
                share: 'Dividir com outra pessoa',
                sync: 'Sincronizar entre celulares',
                other: 'Outra coisa',
            },
            hint: 'Em “Outra coisa”, abrimos a tela de feedback pra você escrever.',
            send: 'Enviar',
            write: 'Escrever pra gente',
            skip: 'Pular',
            thanksTitle: 'Valeu!',
            thanksBody: 'Anotado na nossa lista.',
        },
    },
    trialSummary: {
        pillLastDay: 'Último dia de teste',
        pillUntil: 'Teste até {{date}}',
        title: 'Seu teste em números',
        body: 'Seu Pro termina {{when}}. Isso foi o que você fez com ele:',
        today: 'hoje',
        tomorrow: 'amanhã',
        entries_one: 'lançamento',
        entries_other: 'lançamentos',
        reconciles_one: 'fatura conferida',
        reconciles_other: 'faturas conferidas',
        days_one: 'dia de Pro',
        days_other: 'dias de Pro',
        lossesTitle: 'Quando o Pro acabar',
        lossReport: 'O relatório de {{month}} fica bloqueado',
        lossBackup: 'O backup automático para de rodar',
        lossLimits: 'Criar contas, cartões e metas volta a ter limite',
        footnote: 'Nada é apagado. Seus dados continuam no seu celular.',
        cta: 'Continuar com o Pro',
        ctaCaption: 'Anual: {{annual}} por ano ({{perMonth}}/mês). Você religa a renovação na {{store}}.',
        ctaCaptionNoMonth: 'Anual: {{annual}} por ano. Você religa a renovação na {{store}}.',
        otherPlans: 'Ver outros planos',
        storeErrorTitle: 'Não deu pra abrir a loja agora',
        storeErrorBody: 'Tente de novo em instantes, ou religue em {{location}}.',
    },
    // Modo pausado do iPhone (ADR 0176): doca, Home, folha e aba Mais.
    paused: {
        dockTitle: 'Seu Kontto está pausado',
        dockBody_one: '{{count}} lançamento guardado. Reative pra continuar.',
        dockBody_other: '{{count}} lançamentos guardados. Reative pra continuar.',
        dockBodyNoCount: 'Seus dados estão guardados. Reative pra continuar.',
        dockCta: 'Reativar',
        dockAccessibility: 'Reativar o Kontto',
        lastEntry_one: 'Último lançamento em {{date}} · há {{count}} dia',
        lastEntry_other: 'Último lançamento em {{date}} · há {{count}} dias',
        overlayAccessibility: 'Reativar o Kontto pra mexer nos seus dados',
        lockedTab: '{{tab}}, precisa do Pro',
        lockedAdd: 'Novo lançamento, precisa do Pro',
        pillTrial: 'Teste encerrado em {{date}}',
        pillPro: 'Pro encerrado em {{date}}',
        titleSince: 'Desde então, o Kontto parou',
        title: 'Seu Kontto está pausado',
        body: 'Nada foi apagado. Ele só deixou de acompanhar o seu dinheiro.',
        idleTitle_one: '{{count}} dia sem lançar',
        idleTitle_other: '{{count}} dias sem lançar',
        idleBody: 'O saldo daqui já não bate com o do banco.',
        invoiceTitleToday: 'Fatura do {{card}} fecha hoje',
        invoiceTitleTomorrow: 'Fatura do {{card}} fecha amanhã',
        invoiceTitle: 'Fatura do {{card}} fecha em {{days}} dias',
        invoiceBody: '{{amount}} até agora, sem conferência.',
        reportTitle: 'Relatório de {{month}} travado',
        reportBody: '{{amount}} em despesas, sem ver pra onde foram.',
        backupTitle: 'Backup automático parado',
        backupBody: 'O último foi em {{date}}.',
        benefitsTitle: 'Com o Pro você',
        benefitEntries: 'Lança gastos e receitas sem limite',
        benefitReport: 'Vê o relatório do mês por categoria',
        benefitInvoice: 'Confere a fatura do cartão',
        benefitBackup: 'Tem backup automático dos seus dados',
        planMonthly: 'Mensal',
        planMonthlyHint: 'Cancele quando quiser',
        planAnnual: 'Anual',
        planTrialHint: '{{period}} grátis, depois {{price}} por ano',
        perMonth: '/mês',
        ctaReactivate: 'Reativar o Kontto',
        ctaTrial: 'Começar teste grátis',
        captionMonthly: '{{price}} por mês, renova sozinho. Cancele quando quiser em {{location}}.',
        captionTrial:
            '{{period}} grátis, depois {{price}} por ano, renova sozinho. Cancele antes do fim do teste em {{location}} e não paga nada.',
        allPlans: 'Ver todos os planos',
        restore: 'Restaurar compra',
        terms: 'Termos',
        privacy: 'Privacidade',
        close: 'Fechar',
        moreOpenSection: 'Funciona sem o Pro',
        moreLockedSection: 'Com o Pro',
        moreSettings: 'Configurações',
        moreFeedback: 'Fale comigo',
        moreRestore: 'Restaurar compra',
        moreAccounts: 'Contas e cartões',
        moreCategories: 'Categorias e tags',
        morePlanning: 'Orçamentos e metas',
        moreImport: 'Importar extrato',
        moreBackup: 'Backup e sincronização',
        lockedBadge: 'Pro',
    },
    banner: {
        title: 'Kontto Pro',
        subtitleTrial: 'Experimente grátis: relatórios avançados, temas e tudo sem limite',
        subtitle: 'Relatórios avançados, temas e tudo sem limite',
        trialAccessibility: 'Experimentar o Kontto Pro grátis por {{period}}',
    },
    chips: {
        moreFeatures: '+ {{num}} recursos',
        labels: {
            reports: 'Relatórios + PDF',
            unlimited: 'Tudo sem limite',
            sync: 'Sincronização',
        },
    },
    featuresSheet: {
        title: 'Tudo que o Pro inclui',
    },
    activation: {
        registered: 'registrado',
    },
    proPush: {
        // Empurrão da base free (lib/pro/proPush.ts, ADR 0154). Dois argumentos em A/B,
        // sorteados a cada aparição: `limits` (o paywall mais batido pela base free) e
        // `backup`. Pouco texto de propósito: benefício é só título.
        tenureWeeks_one: 'Sua {{count}} semana de Kontto',
        tenureWeeks_other: 'Suas {{count}} semanas de Kontto',
        tenureMonths_one: 'Seu {{count}} mês de Kontto',
        tenureMonths_other: 'Seus {{count}} meses de Kontto',
        statTransactions_one: 'lançamento',
        statTransactions_other: 'lançamentos',
        statAccounts_one: 'conta',
        statAccounts_other: 'contas',
        statCards_one: 'cartão',
        statCards_other: 'cartões',
        trialTitleLead: 'Teste o Pro',
        trialTitleHighlight: 'grátis',
        // Quem já testou ou assinou e saiu (sem teste grátis): quebra de linha depois de "Kontto".
        comebackTitleLead: 'Seu Kontto',
        comebackTitleHighlight: 'completo de novo',
        limitsHook: 'Você já faz a parte difícil: anotar tudo.',
        backupHook: 'Hoje, tudo isso só existe neste celular.',
        benefitUnlimited: 'Tudo sem limite',
        benefitHome: 'Tela inicial do seu jeito',
        benefitDebts: 'Quem me deve',
        benefitBackup: 'Backup automático',
        benefitSync: 'Sincronização entre aparelhos',
        monthlyTitle: 'Mensal',
        monthlySub: '{{monthly}} por mês',
        annualTrialTitle: '{{period}} grátis',
        annualTrialSub: 'depois {{annual}} por ano · {{perMonth}}/mês',
        annualTrialSubNoMonth: 'depois {{annual}} por ano',
        annualTitle: 'Assinar o Pro Anual',
        annualSub: '{{annual}} por ano · {{perMonth}}/mês',
        annualSubNoMonth: '{{annual}} por ano',
        savingsBadge: 'ECONOMIZE {{pct}}%',
        trialFinePrint: 'Teste sem cobrança · avisamos {{days}} dias antes do fim · cancele quando quiser na {{store}}',
        finePrint: 'Renova automaticamente · cancele quando quiser na {{store}}',
        plansUnavailable: 'Não deu para carregar os planos agora. Tente de novo mais tarde.',
    },
    gate: {
        // Leitor de tela no valor borrado das telas Pro abertas a quem não é Pro (components/pro/BlurredAmount).
        hiddenAmount: 'Valor disponível no Pro',
        limitTitle: 'Recurso do Kontto Pro',
        limitMessage: 'No plano grátis você pode ter até {{limit}} {{label}}. Com o Pro, crie quantos quiser.',
        limits: {
            accounts: 'contas',
            creditCards: 'cartões',
            categories: 'categorias personalizadas',
            budgets: 'orçamentos',
            goals: 'metas e caixinhas',
            tags: 'tags',
            subcategories: 'subcategorias por categoria',
            // Cobre financiamentos E empréstimos: o teto é compartilhado entre os dois
            // (são duas portas pro mesmo módulo — ADR 0055).
            financings: 'contratos ativos (financiamento ou empréstimo)',
        },
        badge: 'PRO',
    },
};
